/**
 * Log-source taxonomy for the session source badge (#660). `uploads.source`
 * records which tool a transcript came from — `claude_code` (the default,
 * everything before 2026-08) or `codex` (OpenAI Codex CLI rollouts). The badge
 * marks NON-default sources so Codex sessions are identifiable at a glance;
 * Claude Code is the default and stays unbadged.
 *
 * This lives in a util module (not a component) so the type→label map is the
 * single source of truth for every consumer (SessionTable, SessionDetail, the
 * Sessions filter) and to satisfy eslint's react-refresh/only-export-components
 * rule, which forbids non-component exports from a component file.
 */

// The default source, which is never badged.
export const DEFAULT_SOURCE = 'claude_code';

// Filter dropdown options on the Sessions page. Kept as a bounded, explicit
// list (like the period tabs) rather than derived from the data — the source
// taxonomy is small and fixed, so it needs no server round-trip.
export const SOURCE_FILTERS: { value: string; label: string }[] = [
  { value: 'claude_code', label: 'Claude Code' },
  { value: 'codex', label: 'Codex' },
];

const SOURCE_LABELS: Record<string, string> = {
  claude_code: 'Claude Code',
  codex: 'Codex',
};

// Human-readable label for a source value. Unknown non-default sources fall
// back to a title-cased version of the raw value (so a future provider still
// renders a sensible badge rather than a blank).
export function sourceLabel(source: string): string {
  if (SOURCE_LABELS[source]) return SOURCE_LABELS[source];
  return source
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

// Whether a source should be badged. Empty/absent and the default source are
// not badged; every other source is.
export function shouldBadgeSource(source?: string): boolean {
  return !!source && source !== DEFAULT_SOURCE;
}
